import { useMemo, useState, useEffect, useCallback } from 'react';
import { message, Modal } from 'antd';
import { useSlotManagement } from './useSlotManagement';
import { useAccounts } from 'src/modules/accounts/provider/accounts.provider';
import { AccountsAPI } from 'src/modules/accounts/api/AccountsApi';

// Helper functions
const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const SLOTS_CACHE_KEY = 'cached_slots_data';

const DEFAULT_VALUES = {
  SLOT_DURATION: 60,
  SLOT_CAPACITY: 1,
  START_TIME: '08:00',
  END_TIME: '20:00'
};

const AVAILABLE_SLOT_DURATIONS = [15, 30, 45, 60];

// Mock Data (could be passed as a dependency in a real app)
// const usedCapacityMocked = [
//   { Day: 'Fri', Date: '17/10/2025', Time: '09:00', UsedCapacity: 2 },
//   { Day: 'Fri', Date: '17/10/2025', Time: '09:30', UsedCapacity: 1 },
//   { Day: 'Fri', Date: '17/10/2025', Time: '11:00', UsedCapacity: 1 },
//   { Day: 'Fri', Date: '17/10/2025', Time: '20:00', UsedCapacity: 2 }
// ];

// const defaultSlotsMocked = [
//   {
//     id: 'Mon',
//     duration: 60,
//     startTime: '08:00',
//     endTime: '20:00',
//     defaultMaxCapacity: 3,
//     slots: [
//       { id: 'Mon-08:00', time: '08:00', capacity: 3, active: true },
//       { id: 'Mon-09:00', time: '09:00', capacity: 3, active: false },
//       { id: 'Mon-10:00', time: '10:00', capacity: 1, active: false },
//       { id: 'Mon-11:00', time: '11:00', capacity: 2, active: false },
//       { id: 'Mon-12:00', time: '12:00', capacity: 1, active: false },
//       { id: 'Mon-13:00', time: '13:00', capacity: 1, active: false },
//       { id: 'Mon-14:00', time: '14:00', capacity: 1, active: false },
//       { id: 'Mon-15:00', time: '15:00', capacity: 1, active: false },
//       { id: 'Mon-16:00', time: '16:00', capacity: 1, active: false },
//       { id: 'Mon-17:00', time: '17:00', capacity: 1, active: false },
//       { id: 'Mon-18:00', time: '18:00', capacity: 1, active: false },
//       { id: 'Mon-19:00', time: '19:00', capacity: 1, active: false }
//     ]
//   }
//   // Other days omitted for brevity
// ];

// API Simulation
const loadCachedSlots = async () => {
  try {
    const cachedData = window.localStorage.getItem(SLOTS_CACHE_KEY);
    return cachedData ? JSON.parse(cachedData) : null;
  } catch (error) {
    console.warn('Failed to load cached slots:', error);
    return null;
  }
};

const fetchSlotsFromBackend = async (userInfo) => {
  console.log('Attempting to fetch slots...');
  // const cachedSlots = await loadCachedSlots();
  // if (cachedSlots) {
  //   console.log('Loaded slots from cache.');
  //   return cachedSlots;
  // }
  console.log('No cached data found, using default mocked data.');
  const slotsFromBE = userInfo?.localBusiness?.carwashSlots;
  console.log('--- SLOTS FROM BE', slotsFromBE);

  // return defaultSlotsMocked;
  return slotsFromBE || [];
};

const fetchUsedCapacityFromBackend = async (userInfo) => {
  console.log('Fetching used capacity from backend...');
  // await new Promise(resolve => setTimeout(resolve, 1000));
  const usedCapacityFromBE = userInfo?.localBusiness?.carwashSlotsUsedCapacity;
  return usedCapacityFromBE || [];
};

export const useCarwashSlotsManager = (initialValue, onChange) => {
  const { userInfo } = useAccounts();
  const [backendSlots, setBackendSlots] = useState(initialValue || []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [openSheet, setOpenSheet] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(DEFAULT_VALUES.SLOT_DURATION);
  const [applyDurationToAll, setApplyDurationToAll] = useState(false);
  const [capacityEditingSlotId, setCapacityEditingSlotId] = useState(null);
  const [selectedCapacity, setSelectedCapacity] = useState(DEFAULT_VALUES.SLOT_CAPACITY);
  const [applyCapacityToAll, setApplyCapacityToAll] = useState(false);
  const [selectedStartTime, setSelectedStartTime] = useState('06:00');
  const [selectedEndTime, setSelectedEndTime] = useState('20:00');
  const [applyStartToAll, setApplyStartToAll] = useState(false);
  const [applyEndToAll, setApplyEndToAll] = useState(false);
  const [usedCapacity, setUsedCapacity] = useState({});
  const [rawUsedCapacity, setRawUsedCapacity] = useState([]);
  const [today] = useState(new Date());
  const [isDirty, setIsDirty] = useState(false);
  const [initialSlotsState, setInitialSlotsState] = useState('');

  const {
    days,
    slotsByDay,
    startTimeByDay,
    endTimeByDay,
    slotDurationByDay,
    dayEnabledByDay,
    updateSlot,
    setDayEnabled,
    toggleAllSlotsForDay,
    toggleAllSlotsForAllDays,
    updateDayTimeConfiguration,
    updateAllDaysTimeConfiguration,
    updateCapacityForDay,
    updateCapacityForAllDays
  } = useSlotManagement({ backendSlots });

  const getDateForDay = useCallback((day) => {
    const dayIndex = days.indexOf(day);
    if (dayIndex === -1) return '';
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + dayIndex);
    const d = futureDate.getDate().toString().padStart(2, '0');
    const m = (futureDate.getMonth() + 1).toString().padStart(2, '0');
    const y = futureDate.getFullYear();
    return `${d}/${m}/${y}`;
  }, [today, days]);

  const loadData = useCallback(async (isRetry = false) => {
    if (isRetry) setIsRetrying(true);
    else setIsLoading(true);
    setError(null);

    try {
      console.log('--- userInfo', userInfo);

      const [slotsData, usedCapacityData] = await Promise.all([
        fetchSlotsFromBackend(userInfo),
        fetchUsedCapacityFromBackend(userInfo)
      ]);
      setBackendSlots(slotsData);
      setRawUsedCapacity(usedCapacityData);

      // Capture initial state for dirty checking
      const initialState = JSON.stringify((slotsData || []).map(d => ({
        id: d.id,
        duration: d.duration,
        startTime: d.startTime,
        endTime: d.endTime,
        dayEnabled: d.dayEnabled ?? d.slots?.some(s => s.active) ?? false,
        slots: d.slots.map(s => ({ id: s.id, capacity: s.capacity, active: s.active }))
      })));
      setInitialSlotsState(initialState);
      setIsDirty(false);

      if (isRetry) message.success('Successfully reloaded data');
    } catch (e) {
      const err = e;
      setError(err);
      message.error(err.message);
      const cachedData = await loadCachedSlots();
      if (cachedData) {
        setBackendSlots(cachedData);
        // Capture initial state for dirty checking
        const initialState = JSON.stringify(cachedData.map(d => ({
          id: d.id,
          duration: d.duration,
          startTime: d.startTime,
          endTime: d.endTime,
          dayEnabled: d.dayEnabled ?? d.slots?.some(s => s.active) ?? false,
          slots: d.slots.map(s => ({ id: s.id, capacity: s.capacity, active: s.active }))
        })));
        setInitialSlotsState(initialState);
        setIsDirty(false);
        message.warning('Using cached data due to error');
        throw err;
      }
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  }, [userInfo]);

  useEffect(() => {
    const processedCapacity = {};
    const currentSlotsByDay = backendSlots.reduce((acc, dayConfig) => {
      const dayKey = dayConfig.id;
      // Use the slots from the useSlotManagement hook's state as it's the most current
      const stateSlots = slotsByDay[dayKey];
      acc[dayKey] = (stateSlots && stateSlots.length > 0)
        ? stateSlots.map(s => s.time).sort((a, b) => a.localeCompare(b))
        : []; // If no slots, it's an empty array
      return acc;
    }, {});

    // Build a map of date -> day name for this week only
    const dateToDay = {};
    for (const day of days) {
      const date = getDateForDay(day);
      if (date) dateToDay[date] = day;
    }

    rawUsedCapacity.forEach((item) => {
      // Match by exact date — only this week's bookings
      const day = dateToDay[item.Date];
      if (!day) return;

      const daySlots = currentSlotsByDay[day];

      let targetTime = item.Time;
      let isOrphan = true;

      if (daySlots && daySlots.length > 0) {
        const itemMinutes = timeToMinutes(item.Time);
        const dayStartMinutes = timeToMinutes(startTimeByDay[day] || '00:00');
        const dayEndMinutes = timeToMinutes(endTimeByDay[day] || '23:59');

        if (itemMinutes >= dayStartMinutes && itemMinutes < dayEndMinutes) {
          for (let i = daySlots.length - 1; i >= 0; i--) {
            if (timeToMinutes(daySlots[i]) <= itemMinutes) {
              targetTime = daySlots[i];
              isOrphan = false;
              break;
            }
          }
        }
      }

      if (!isOrphan) {
        const key = `${item.Date}-${targetTime}`;
        processedCapacity[key] = (processedCapacity[key] || 0) + item.UsedCapacity;
      }
    });

    setUsedCapacity(processedCapacity);
  }, [rawUsedCapacity, slotsByDay, startTimeByDay, endTimeByDay, backendSlots, days, getDateForDay]);

  const buildCarwashSlotsPayload = useCallback(() => {
    return days.map(day => ({
      id: day,
      duration: slotDurationByDay[day] || DEFAULT_VALUES.SLOT_DURATION,
      startTime: startTimeByDay[day] || DEFAULT_VALUES.START_TIME,
      endTime: endTimeByDay[day] || DEFAULT_VALUES.END_TIME,
      dayEnabled: dayEnabledByDay[day] ?? false,
      defaultMaxCapacity: slotsByDay[day]?.[0]?.capacity ?? DEFAULT_VALUES.SLOT_CAPACITY,
      slots: (slotsByDay[day] || []).map(({ id, time, capacity, active, duration }) => ({
        id, time, capacity, active, duration
      }))
    }));
  }, [days, slotDurationByDay, startTimeByDay, endTimeByDay, dayEnabledByDay, slotsByDay]);

  useEffect(() => {
    if (isLoading || !initialSlotsState) return;

    const currentState = JSON.stringify(days.map(day => ({
      id: day,
      duration: slotDurationByDay[day],
      startTime: startTimeByDay[day],
      endTime: endTimeByDay[day],
      dayEnabled: dayEnabledByDay[day] ?? false,
      slots: (slotsByDay[day] || []).map(s => ({ id: s.id, capacity: s.capacity, active: s.active }))
    })));

    const dirty = initialSlotsState !== currentState;
    setIsDirty(dirty);

    if (dirty) {
      onChange(buildCarwashSlotsPayload());
    }
  }, [slotsByDay, startTimeByDay, endTimeByDay, slotDurationByDay, dayEnabledByDay, initialSlotsState, days, onChange, isLoading, buildCarwashSlotsPayload]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedDay && days.length > 0) setSelectedDay(days[0]);
  }, [days, selectedDay]);

  // Initialize modal values when opening
  useEffect(() => {
    if (!openSheet || !selectedDay) return;
    if (openSheet === 'start') {
      setSelectedStartTime(startTimeByDay[selectedDay] || DEFAULT_VALUES.START_TIME);
    } else if (openSheet === 'end') {
      setSelectedEndTime(endTimeByDay[selectedDay] || DEFAULT_VALUES.END_TIME);
    } else if (openSheet === 'duration') {
      setSelectedDuration(slotDurationByDay[selectedDay] || DEFAULT_VALUES.SLOT_DURATION);
    } else if (openSheet === 'default_capacity') {
      setSelectedCapacity(slotsByDay[selectedDay]?.[0]?.capacity ?? DEFAULT_VALUES.SLOT_CAPACITY);
    }
  }, [openSheet, selectedDay, startTimeByDay, endTimeByDay, slotDurationByDay, slotsByDay]);

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      const dataToSave = buildCarwashSlotsPayload();

      const lb = userInfo.localBusiness;
      const facebookService = lb.socialServices?.find(s => s.key === 'FACEBOOK');
      const instagramService = lb.socialServices?.find(s => s.key === 'INSTAGRAM');
      const facebookValue = formData?.facebook || facebookService?.value;
      const instagramValue = formData?.instagram || instagramService?.value;
      const social = Object.assign(
        {},
        facebookValue ? { facebook: { link: facebookValue } } : null,
        instagramValue ? { instagram: { link: instagramValue } } : null
      );

      await AccountsAPI.updateAccount(lb.id, {
        schedule: lb.schedule || [],
        email: formData?.email || lb.email,
        telephone: formData?.phoneNumber || lb.telephone,
        description: formData?.description || lb.description,
        greeting: formData?.greeting || lb.greeting,
        greetings: (formData?.greeting || lb.greeting) ? { en: formData?.greeting || lb.greeting, it: formData?.greeting || lb.greeting } : lb.greetings,
        websiteUrl: formData?.website || lb.websiteUrl,
        xposition: formData?.xPosition ?? lb.xposition,
        yposition: formData?.yPosition ?? lb.yposition,
        closingPeriods: formData?.closingPeriods
          ? formData.closingPeriods
              .filter(p => p?.start)
              .map(p => ({ start: p.start.format('YYYY-MM-DD'), end: p.end?.format('YYYY-MM-DD') }))
          : lb.closingPeriods ?? [],
        social,
        carwashSlots: dataToSave
      });

      // After saving, the current state becomes the new "clean" state.
      const newInitialState = JSON.stringify(dataToSave.map(d => ({
        id: d.id,
        duration: d.duration,
        startTime: d.startTime,
        endTime: d.endTime,
        dayEnabled: d.dayEnabled,
        slots: d.slots.map(s => ({ id: s.id, capacity: s.capacity, active: s.active }))
      })));
      setInitialSlotsState(newInitialState);
      setIsDirty(false);

      message.success('Saved successfully!');
    } catch (e) {
      console.error('Failed to save carwash slots:', e);
      message.error('Failed to save.');
    } finally {
      setIsSaving(false);
    }
  };

  const slotsForSelectedDay = useMemo(() => (selectedDay ? slotsByDay[selectedDay] || [] : []), [selectedDay, slotsByDay]);
  const isDayEnabled = useMemo(() => selectedDay ? dayEnabledByDay[selectedDay] ?? false : false, [selectedDay, dayEnabledByDay]);
  const [areAllSlotsEnabled, setAreAllSlotsEnabled] = useState(false);

  // "All Days" is ON only when every day is enabled, OFF otherwise.
  useEffect(() => {
    if (days.length === 0) return;
    const allEnabled = days.every(day => dayEnabledByDay[day] ?? false);
    setAreAllSlotsEnabled(allEnabled);
  }, [dayEnabledByDay, days]);

  const orphanSlots = useMemo(() => {
    const result = {
      before: { count: 0, slots: [] },
      after: { count: 0, slots: [] }
    };
    if (!selectedDay) return result;

    const dateForSelectedDay = getDateForDay(selectedDay);
    const startTime = startTimeByDay[selectedDay];
    const endTime = endTimeByDay[selectedDay];

    if (!dateForSelectedDay || !startTime || !endTime) return result;

    rawUsedCapacity.forEach((item) => {
      // Match by exact date, not just day of week — avoids showing
      // last week's bookings on this week's day.
      if (item.Date !== dateForSelectedDay) return;

      const itemMinutes = timeToMinutes(item.Time);
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);

      if (itemMinutes < startMinutes) {
        result.before.count += item.UsedCapacity;
        const existing = result.before.slots.find(s => s.time === item.Time);
        if (existing) {
          existing.capacity += item.UsedCapacity;
        } else {
          result.before.slots.push({ time: item.Time, capacity: item.UsedCapacity });
        }
      } else if (itemMinutes >= endMinutes) {
        result.after.count += item.UsedCapacity;
        const existing = result.after.slots.find(s => s.time === item.Time);
        if (existing) {
          existing.capacity += item.UsedCapacity;
        } else {
          result.after.slots.push({ time: item.Time, capacity: item.UsedCapacity });
        }
      }
    });

    result.before.slots.sort((a, b) => a.time.localeCompare(b.time));
    result.after.slots.sort((a, b) => a.time.localeCompare(b.time));
    return result;
  }, [selectedDay, rawUsedCapacity, startTimeByDay, endTimeByDay, getDateForDay]);

  const handleToggleAllSlotsForAllDays = (enabled) => {
    Modal.confirm({
      title: `Confirm ${enabled ? 'Enabling' : 'Disabling'} All Slots`,
      content: `Are you sure you want to ${enabled ? 'enable' : 'disable'} all slots for all days? This will override existing settings.`,
      onOk: () => {
        setAreAllSlotsEnabled(enabled);
        toggleAllSlotsForAllDays(enabled);
      }
    });
  };

  const handleCapacityPress = useCallback((slotId, capacity) => {
    setCapacityEditingSlotId(slotId);
    setSelectedCapacity(capacity);
    setOpenSheet('capacity');
  }, []);

  const handleToggleActive = useCallback((day, slotId, active) => {
    updateSlot(day, slotId, { active });
    if (active) {
      // Activating any slot enables the day
      setDayEnabled(day, true);
    } else {
      // Deactivating a slot: if no other slot is still active, disable the day
      const daySlots = slotsByDay[day] || [];
      const anyStillActive = daySlots.some(s => s.id !== slotId && s.active);
      if (!anyStillActive) {
        setDayEnabled(day, false);
      }
    }
  }, [updateSlot, setDayEnabled, slotsByDay]);

  const handleToggleAllSlots = useCallback((enabled) => {
    if (selectedDay) toggleAllSlotsForDay(selectedDay, enabled);
  }, [selectedDay, toggleAllSlotsForDay]);

  const handleModalSave = () => {
    if (selectedDay) {
      if (openSheet === 'duration') {
        if (applyDurationToAll) updateAllDaysTimeConfiguration({ duration: selectedDuration });
        else updateDayTimeConfiguration(selectedDay, { duration: selectedDuration });
      }
      if (openSheet === 'start') {
        if (timeToMinutes(selectedStartTime) >= timeToMinutes(endTimeByDay[selectedDay])) {
          message.error('Start time cannot be after end time.');
          return;
        }
        if (applyStartToAll) updateAllDaysTimeConfiguration({ start: selectedStartTime });
        else updateDayTimeConfiguration(selectedDay, { start: selectedStartTime });
      }
      if (openSheet === 'end') {
        if (timeToMinutes(startTimeByDay[selectedDay]) >= timeToMinutes(selectedEndTime)) {
          message.error('End time cannot be before start time.');
          return;
        }
        if (applyEndToAll) updateAllDaysTimeConfiguration({ end: selectedEndTime });
        else updateDayTimeConfiguration(selectedDay, { end: selectedEndTime });
      }
      if (openSheet === 'capacity' && capacityEditingSlotId) {
        updateSlot(selectedDay, capacityEditingSlotId, { capacity: selectedCapacity });
      }
      if (openSheet === 'default_capacity') {
        if (applyCapacityToAll) {
          updateCapacityForAllDays(selectedCapacity);
        } else {
          updateCapacityForDay(selectedDay, selectedCapacity);
        }
      }
    }
    setOpenSheet(null);
    setApplyDurationToAll(false);
    setApplyStartToAll(false);
    setApplyEndToAll(false);
    setApplyCapacityToAll(false);
    setCapacityEditingSlotId(null);
  };

  const generateTimes = useCallback((forEndTime = false, startTime = '00:00') => {
    const times = [];
    for (let h = 6; h <= 22; h++) {
      times.push(`${String(h).padStart(2, '0')}:00`);
      times.push(`${String(h).padStart(2, '0')}:30`);
    }
    if (forEndTime) {
      const startMinutes = timeToMinutes(startTime);
      return times.filter(t => timeToMinutes(t) > startMinutes);
    }
    return times;
  }, []);

  return {
    isLoading,
    isSaving,
    isRetrying,
    error,
    days,
    selectedDay,
    setSelectedDay,
    slotsForSelectedDay,
    isDayEnabled,
    areAllSlotsEnabled,
    slotDurationByDay,
    startTimeByDay,
    endTimeByDay,
    openSheet,
    setOpenSheet,
    selectedDuration,
    setSelectedDuration,
    applyDurationToAll,
    setApplyDurationToAll,
    selectedStartTime,
    setSelectedStartTime,
    applyStartToAll,
    setApplyStartToAll,
    selectedEndTime,
    setSelectedEndTime,
    applyEndToAll,
    setApplyEndToAll,
    selectedCapacity,
    setSelectedCapacity,
    applyCapacityToAll,
    setApplyCapacityToAll,
    capacityEditingSlotId,
    usedCapacity,
    orphanSlots,
    backendSlots,
    isDirty,
    setIsDirty,
    DEFAULT_VALUES,
    AVAILABLE_SLOT_DURATIONS,
    getDateForDay,
    loadData,
    handleSave,
    buildCarwashSlotsPayload,
    handleToggleAllSlotsForAllDays,
    handleCapacityPress,
    handleToggleActive,
    handleToggleAllSlots,
    handleModalSave,
    generateTimes
  };
};
